import React from 'react';
import {
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * TestResultsFilters - Filter and sort controls for test results with cascading logic
 * Each dropdown only shows options that exist given the current filter selections
 * @param {Array} results - All available results for extracting unique filter values
 * @param {Object} filters - Current filter values
 * @param {Function} onFilterChange - Callback when filter changes
 * @param {Function} onClearFilters - Callback to clear all filters
 */
export default function TestResultsFilters({ results, filters, onFilterChange, onClearFilters }) {
  // Extract unique values from results for dropdowns with cascading logic
  // Each dropdown only shows values that exist given the current filter selections

  // For flow types: filter by everything EXCEPT flowName
  const flowFilteredResults = results.filter((r) => {
    if (filters.testCaseName && r.test_case_name !== filters.testCaseName) return false;
    if (filters.deviceName && r.device_name !== filters.deviceName) return false;
    if (filters.userEmail && r.user_email !== filters.userEmail) return false;
    if (filters.market && r.market !== filters.market) return false;
    return true;
  });
  const uniqueFlows = [...new Set(flowFilteredResults.map((r) => r.flow_name))].sort();

  // For test cases: filter by everything EXCEPT testCaseName
  const testCaseFilteredResults = results.filter((r) => {
    if (filters.flowName && r.flow_name !== filters.flowName) return false;
    if (filters.deviceName && r.device_name !== filters.deviceName) return false;
    if (filters.userEmail && r.user_email !== filters.userEmail) return false;
    if (filters.market && r.market !== filters.market) return false;
    return true;
  });
  const uniqueTestCases = [...new Set(testCaseFilteredResults.map((r) => r.test_case_name))].sort();

  // For devices: filter by everything EXCEPT deviceName
  const deviceFilteredResults = results.filter((r) => {
    if (filters.flowName && r.flow_name !== filters.flowName) return false;
    if (filters.testCaseName && r.test_case_name !== filters.testCaseName) return false;
    if (filters.userEmail && r.user_email !== filters.userEmail) return false;
    if (filters.market && r.market !== filters.market) return false;
    return true;
  });
  const uniqueDevices = [...new Set(deviceFilteredResults.map((r) => r.device_name))].sort();

  // For users: filter by everything EXCEPT userEmail
  const userFilteredResults = results.filter((r) => {
    if (filters.flowName && r.flow_name !== filters.flowName) return false;
    if (filters.testCaseName && r.test_case_name !== filters.testCaseName) return false;
    if (filters.deviceName && r.device_name !== filters.deviceName) return false;
    if (filters.market && r.market !== filters.market) return false;
    return true;
  });
  const uniqueUsers = [...new Set(userFilteredResults.map((r) => r.user_email))].sort();

  // For markets: filter by everything EXCEPT market
  const marketFilteredResults = results.filter((r) => {
    if (filters.flowName && r.flow_name !== filters.flowName) return false;
    if (filters.testCaseName && r.test_case_name !== filters.testCaseName) return false;
    if (filters.deviceName && r.device_name !== filters.deviceName) return false;
    if (filters.userEmail && r.user_email !== filters.userEmail) return false;
    return true;
  });
  const uniqueMarkets = [...new Set(marketFilteredResults.map((r) => r.market))].sort();

  const handleChange = (field) => (event) => {
    onFilterChange(field, event.target.value);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.flowName ||
    filters.testCaseName ||
    filters.deviceName ||
    filters.userEmail ||
    filters.market ||
    filters.sortBy !== 'newest';

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FilterAltIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Filter Results
        </Typography>
      </Box>

      {/* Filter Dropdowns Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 2,
          mb: 2,
        }}
      >
        {/* Flow Type Filter */}
        <FormControl
          size="small"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <InputLabel>Flow Type</InputLabel>
          <Select value={filters.flowName} onChange={handleChange('flowName')} label="Flow Type">
            <MenuItem value="">All</MenuItem>
            {uniqueFlows.map((flow) => (
              <MenuItem key={flow} value={flow}>
                {flow}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Test Case Filter */}
        <FormControl
          size="small"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <InputLabel>Test Case</InputLabel>
          <Select
            value={filters.testCaseName}
            onChange={handleChange('testCaseName')}
            label="Test Case"
          >
            <MenuItem value="">All</MenuItem>
            {uniqueTestCases.map((testCase) => (
              <MenuItem key={testCase} value={testCase}>
                {testCase}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Device Name Filter */}
        <FormControl
          size="small"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <InputLabel>Device</InputLabel>
          <Select value={filters.deviceName} onChange={handleChange('deviceName')} label="Device">
            <MenuItem value="">All</MenuItem>
            {uniqueDevices.map((device) => (
              <MenuItem key={device} value={device}>
                {device}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* User Email Filter */}
        <FormControl
          size="small"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <InputLabel>User</InputLabel>
          <Select value={filters.userEmail} onChange={handleChange('userEmail')} label="User">
            <MenuItem value="">All</MenuItem>
            {uniqueUsers.map((user) => (
              <MenuItem key={user} value={user}>
                {user}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Market Filter */}
        <FormControl
          size="small"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <InputLabel>Market</InputLabel>
          <Select value={filters.market} onChange={handleChange('market')} label="Market">
            <MenuItem value="">All</MenuItem>
            {uniqueMarkets.map((market) => (
              <MenuItem key={market} value={market}>
                {market}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Clear Filters Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: '36px' }}>
        {hasActiveFilters && (
          <Button
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Clear Filters
          </Button>
        )}
      </Box>
    </Paper>
  );
}
